import React, { useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import NoticeIcon from '../../components/NoticeIcon/NoticeIcon';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalTextarea } from '../../components/FormField/PortalInput';
import { useToast } from '../../components/Toast/ToastContext';
import type { ButtonVariant } from '../../components/Button/Button';
import {
  partnerMarkDeviceInoperableMutation,
  partnerMarkDeviceLostMutation,
  partnerRestoreDeviceToInventoryMutation,
  type PartnerInventoryDeviceVariables,
} from '../../lib/rescueQueries';
import type { RescueInventoryState } from '../../lib/rescueQueries';
import styles from './Devices.module.scss';

export type DeviceStatusChange = 'lost' | 'inoperable' | 'found';

export interface MarkDeviceStatusTarget {
  change: DeviceStatusChange;
  moduleId: string;
  petName?: string | null;
}

/** Collars on the partner's books and in service. Lost, inoperable and departed collars aren't the rescue's to mark. */
const MARKABLE_STATES: ReadonlySet<RescueInventoryState> = new Set([
  'IN_TRANSIT',
  'IN_STOCK',
  'ON_PARTNER_PET',
  'FOSTERED',
  'PENDING_ADOPTION',
]);

export function canMarkLostOrInoperable(state: RescueInventoryState | null | undefined): boolean {
  return !!state && MARKABLE_STATES.has(state);
}

/** The server's refusals, in the portal's words. */
export function deviceStatusErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : '';
  if (message.includes('contact Fi support')) {
    return "Fi has already set this collar's status. Contact Fi support to change it.";
  }
  if (message.includes('outside the partner')) {
    return "This collar is on a pet outside your rescue, so you can't change its status.";
  }
  if (message.includes('Lost Dog Mode')) {
    return "This collar is in Lost Dog Mode. Turn Lost Dog Mode off before changing the collar's status.";
  }
  if (message.includes('pending adoption')) {
    return "This collar's pet has a pending adoption. Cancel the adoption before marking the collar lost.";
  }
  return "Couldn't update this collar. Try again, or contact Fi support if it keeps happening.";
}

interface ChangeCopy {
  title: string;
  confirm: string;
  confirmVariant: ButtonVariant;
  done: string;
  notice: (petName?: string | null) => string;
}

const COPY: Record<DeviceStatusChange, ChangeCopy> = {
  lost: {
    title: 'Mark device lost',
    confirm: 'Mark lost',
    confirmVariant: 'danger',
    done: 'marked lost',
    notice: (petName) =>
      `${petName ? `It will be removed from ${petName} and moved` : 'It will move'} to your Lost list. If it turns up, mark it found there.`,
  },
  inoperable: {
    title: 'Mark device inoperable',
    confirm: 'Mark inoperable',
    confirmVariant: 'danger',
    done: 'marked inoperable',
    notice: () => "This can't be undone from the portal. Only Fi support can restore an inoperable device.",
  },
  found: {
    title: 'Mark device found',
    confirm: 'Mark found',
    confirmVariant: 'green',
    done: 'marked found',
    notice: () => 'It goes back to your stock, ready to add to a pet.',
  },
};

// The API answers the device lists from a read replica, which can trail the mutation's write by a few hundred
// milliseconds. Refetching straight away could hand back the old lists, e.g. a collar marked found missing from Stock.
const REPLICA_SETTLE_MS = 500;

const MUTATIONS = {
  lost: partnerMarkDeviceLostMutation,
  inoperable: partnerMarkDeviceInoperableMutation,
  found: partnerRestoreDeviceToInventoryMutation,
} as const;

interface MarkDeviceStatusModalProps {
  target: MarkDeviceStatusTarget | null;
  onClose: () => void;
}

export default function MarkDeviceStatusModal({ target, onClose }: MarkDeviceStatusModalProps) {
  const { showToast } = useToast();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const client = useApolloClient();
  const [refreshing, setRefreshing] = useState(false);
  const [markLost, lostState] = useMutation(MUTATIONS.lost);
  const [markInoperable, inoperableState] = useMutation(MUTATIONS.inoperable);
  const [markFound, foundState] = useMutation(MUTATIONS.found);
  const submitting = lostState.loading || inoperableState.loading || foundState.loading || refreshing;

  if (!target) {
    return null;
  }
  const copy = COPY[target.change];

  function handleClose() {
    if (submitting) {
      return;
    }
    setNote('');
    setError(null);
    onClose();
  }

  async function handleConfirm() {
    if (!target || submitting) {
      return;
    }
    const variables: PartnerInventoryDeviceVariables = {
      input: { moduleId: target.moduleId, note: note.trim() || null },
    };
    try {
      const mutate = { lost: markLost, inoperable: markInoperable, found: markFound }[target.change];
      await mutate({ variables });
      setRefreshing(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, REPLICA_SETTLE_MS));
        // A failed refetch shows on the table it belongs to; the change itself went through.
        await client.refetchQueries({ include: 'active' }).catch(() => undefined);
      } finally {
        setRefreshing(false);
      }
      showToast(`${target.moduleId} ${copy.done}`);
      setNote('');
      setError(null);
      onClose();
    } catch (err) {
      setError(deviceStatusErrorMessage(err));
    }
  }

  return (
    <Modal open onClose={handleClose}>
      <ModalTitle>{copy.title}</ModalTitle>
      <p className={styles.modalDescription}>
        Device <b>{target.moduleId}</b>
        {target.petName ? ` (${target.petName})` : ''}.
      </p>
      <PortalField label="Note (optional)" hint="Only your rescue and Fi can see this.">
        <PortalTextarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          disabled={submitting}
          aria-label="Note"
        />
      </PortalField>
      {target.change === 'found' ? (
        <div className={`${styles.notice} ${styles.noticeInfo}`}>
          <NoticeIcon variant="info" />
          <div>{copy.notice(target.petName)}</div>
        </div>
      ) : (
        <div className={`${styles.notice} ${styles.noticeCaution}`}>
          <NoticeIcon variant="caution" />
          <div>{copy.notice(target.petName)}</div>
        </div>
      )}
      {error && (
        <p className={styles.modalError} role="alert">
          {error}
        </p>
      )}
      <ModalActions>
        <Button variant="ghost" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant={copy.confirmVariant} onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Working…' : copy.confirm}
        </Button>
      </ModalActions>
    </Modal>
  );
}
